import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { setAuthUser } from '../utils/userData';
import './job-login.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const DEMO_EMAIL = 'demo123@gmail.com';
const DEMO_PASSWORD = 'demo123';

const JobLogin = () => {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		email: '',
		password: ''
	});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const fillDemoCredentials = () => {
		setFormData({ email: DEMO_EMAIL, password: DEMO_PASSWORD });
		toast.info('Demo credentials added. Click login to continue.');
	};

	const handleLogin = async (e) => {
		e.preventDefault();

		if (!formData.email || !formData.password) {
			toast.error('Please enter both email and password.');
			return;
		}

		if (formData.email === DEMO_EMAIL && formData.password === DEMO_PASSWORD) {
			setAuthUser({
				email: DEMO_EMAIL,
				fullName: 'Demo User',
				loginType: 'demo'
			});
			toast.success('Demo login successful. Welcome back!');
			navigate('/jobs');
			return;
		}

		try {
			setIsSubmitting(true);
			const response = await axios.post(`${API_BASE_URL}/job-login`, {
				email: formData.email,
				password: formData.password
			});

			setAuthUser({
				email: formData.email,
				fullName: response.data?.fullName || response.data?.name || formData.email.split('@')[0],
				loginType: 'api'
			});

			toast.success('Login successful. Welcome back!');
			navigate('/jobs');
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || 'Unable to login. Please check your credentials.';
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="auth-page">
			<Navbar />

			<section className="auth-section">
				<motion.div
					className="auth-ornament auth-ornament-left"
					animate={{ y: [0, -20, 0], opacity: [0.6, 1, 0.6] }}
					transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
				/>
				<motion.div
					className="auth-ornament auth-ornament-right"
					animate={{ y: [0, 20, 0], opacity: [0.5, 0.9, 0.5] }}
					transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
				/>

				<motion.div
					className="auth-card"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, ease: 'easeOut' }}
				>
					<motion.h1
						className="auth-title"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.2, duration: 0.5 }}
					>
						Member Login
					</motion.h1>

					<motion.p
						className="auth-subtitle"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.3, duration: 0.5 }}
					>
						Access your premium dashboard and opportunities.
					</motion.p>

					<motion.form
						className="auth-form"
						onSubmit={handleLogin}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.4, duration: 0.5 }}
					>
						<label htmlFor="email">Email</label>
						<input
							id="email"
							name="email"
							type="email"
							value={formData.email}
							onChange={handleChange}
							placeholder="you@example.com"
							autoComplete="email"
						/>

						<label htmlFor="password">Password</label>
						<input
							id="password"
							name="password"
							type="password"
							value={formData.password}
							onChange={handleChange}
							placeholder="Enter your password"
							autoComplete="current-password"
						/>

						<button className="auth-submit" type="submit" disabled={isSubmitting}>
							{isSubmitting ? 'Signing In...' : 'Sign In'}
						</button>

						<button className="auth-demo" type="button" onClick={fillDemoCredentials}>
							Use Demo Login
						</button>
					</motion.form>

					<motion.div
						className="auth-meta"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.5, duration: 0.5 }}
					>
						<p>
							No account yet? <Link to="/job-signup">Create one</Link>
						</p>
						<p className="demo-text">
							Demo: {DEMO_EMAIL} / {DEMO_PASSWORD}
						</p>
					</motion.div>
				</motion.div>
			</section>

			<Footer />
		</div>
	);
};

export default JobLogin;
